import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { session, tenantContext, SESSION_EXPIRED_EVENT, useCurrentUser, useLogin, useLogout } from '@/shared/api';
import type { User, LoginRequest, Permission, UserRole } from '@/shared/api/types';

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  checkRole: (roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isInitialized, setIsInitialized] = useState(false);

  // Get current user if authenticated
  const {
    data: user,
    isLoading: isUserLoading,
    error: userError,
  } = useCurrentUser();

  const loginMutation = useLogin();
  const logoutMutation = useLogout();

  const isAuthenticated = !!user && !userError;
  const isLoading = !isInitialized || isUserLoading;

  // Handle session expired event
  useEffect(() => {
    const handleSessionExpired = () => {
      queryClient.clear();
      tenantContext.clearAll();

      // Redirect to login with return path
      const returnPath = location.pathname !== '/login' ? location.pathname : '/app';
      navigate(`/login?returnTo=${encodeURIComponent(returnPath)}`);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [navigate, location.pathname, queryClient]);

  // Mark initialized once we've checked auth
  useEffect(() => {
    if (!isUserLoading) {
      setIsInitialized(true);
    }
  }, [isUserLoading]);

  // Login function
  const login = useCallback(
    async (credentials: LoginRequest) => {
      const response = await loginMutation.mutateAsync(credentials);
      session.setToken(response.session_token);
      // W0 workspace bridge: the backend honors X-Workspace-ID, so a stale
      // workspace from a previous session must never leak into the new one
      tenantContext.clearWorkspaceId();

      // Redirect to return path or default
      const params = new URLSearchParams(location.search);
      const returnTo = params.get('returnTo') || '/app';
      navigate(returnTo);
    },
    [loginMutation, navigate, location.search]
  );

  // Logout function
  const logout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync();
    } finally {
      session.clearToken();
      tenantContext.clearAll();
      queryClient.clear();
      navigate('/login');
    }
  }, [logoutMutation, navigate, queryClient]);

  // Permission check using role-based permissions
  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (!user) return false;
      const rolePermissions = getRolePermissions(user.role);
      return rolePermissions.includes(permission);
    },
    [user]
  );

  const hasAnyPermission = useCallback(
    (permissions: Permission[]): boolean => {
      return permissions.some((p) => hasPermission(p));
    },
    [hasPermission]
  );

  const hasAllPermissions = useCallback(
    (permissions: Permission[]): boolean => {
      return permissions.every((p) => hasPermission(p));
    },
    [hasPermission]
  );

  const checkRole = useCallback(
    (roles: UserRole[]): boolean => {
      if (!user) return false;
      return roles.includes(user.role);
    },
    [user]
  );

  const value: AuthContextValue = {
    user: user ?? null,
    isLoading,
    isAuthenticated,
    login,
    logout,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    checkRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Helper to get permissions for a role
export function getRolePermissions(role: UserRole): Permission[] {
  const permissions: Record<UserRole, Permission[]> = {
    platform_admin: [
      'view:dashboard',
      'view:clients',
      'manage:clients',
      'view:data',
      'manage:data',
      'view:audiences',
      'create:audiences',
      'manage:audiences',
      'view:activations',
      'create:activations',
      'manage:activations',
      'view:campaigns',
      'create:campaigns',
      'manage:campaigns',
      'view:analytics',
      'view:integrations',
      'manage:integrations',
      'view:reports',
      'create:reports',
      'view:admin',
      'manage:admin',
      'view:settings',
      'manage:settings',
    ],
    org_admin: [
      'view:dashboard',
      'view:clients',
      'manage:clients',
      'view:data',
      'manage:data',
      'view:audiences',
      'create:audiences',
      'manage:audiences',
      'view:activations',
      'create:activations',
      'manage:activations',
      'view:campaigns',
      'create:campaigns',
      'manage:campaigns',
      'view:analytics',
      'view:integrations',
      'manage:integrations',
      'view:reports',
      'create:reports',
      'view:admin',
      'view:settings',
      'manage:settings',
    ],
    client_admin: [
      'view:dashboard',
      'view:clients',
      'view:data',
      'manage:data',
      'view:audiences',
      'create:audiences',
      'manage:audiences',
      'view:activations',
      'create:activations',
      'manage:activations',
      'view:campaigns',
      'create:campaigns',
      'manage:campaigns',
      'view:analytics',
      'view:integrations',
      'manage:integrations',
      'view:reports',
      'create:reports',
      'view:settings',
      'manage:settings',
    ],
    workspace_admin: [
      'view:dashboard',
      'view:data',
      'manage:data',
      'view:audiences',
      'create:audiences',
      'manage:audiences',
      'view:activations',
      'create:activations',
      'view:campaigns',
      'create:campaigns',
      'view:analytics',
      'view:integrations',
      'view:reports',
      'create:reports',
      'view:settings',
    ],
    analyst: [
      'view:dashboard',
      'view:data',
      'view:audiences',
      'create:audiences',
      'view:activations',
      'view:campaigns',
      'view:analytics',
      'view:reports',
      'create:reports',
      'view:settings',
    ],
    viewer: [
      'view:dashboard',
      'view:data',
      'view:audiences',
      'view:campaigns',
      'view:analytics',
      'view:reports',
      'view:settings',
    ],
  };

  // Backend session principals carry legacy/service roles that predate this
  // granular map: workspace-admin keys and session org-admins report "admin";
  // self-signup grants "marketing_owner"; demo sessions report "demo".
  // Without these aliases a logged-in admin gets "Access Denied" on every
  // permission-gated page (release-blocker, 2026-08-12). Normalize them.
  const ALIASES: Record<string, UserRole> = {
    admin: 'platform_admin',       // full operator (backend maps 'admin' -> '*')
    marketing_owner: 'org_admin',  // signup default
    org_owner: 'org_admin',
    member: 'analyst',
    demo: 'viewer',
  };
  const resolved = (permissions[role] ? role : ALIASES[role as string]) as UserRole;
  return permissions[resolved] || permissions.viewer;
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
